'use client';

import * as React from 'react';
import { motion, useSpring, useInView } from 'motion/react';
import { cn } from '../../lib/utils';

interface AnimatedNumberProps {
  value: number;
  className?: string;
}

const AnimatedNumber = ({ value, className = "" }: AnimatedNumberProps) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });
  const spring = useSpring(0, { damping: 30, stiffness: 100, mass: 1 });

  React.useEffect(() => {
    if (isInView) {
      spring.set(value);
    }
  }, [spring, isInView, value]);

  React.useEffect(() => {
    const unsubscribe = spring.on('change', (latest) => {
      if (ref.current) {
        ref.current.textContent = Math.floor(latest).toString();
      }
    });
    return () => unsubscribe();
  }, [spring]);

  return <span ref={ref} className={className}>0</span>;
};

interface LevelProgressProps {
  currentLevel?: number;
  currentPoints?: number;
  pointsForNextLevel?: number;
  userName?: string;
  className?: string;
}

export function LevelProgress({
  currentLevel = 1,
  currentPoints = 0,
  pointsForNextLevel = 100,
  userName = 'Usuário',
  className = '',
}: LevelProgressProps) {
  const [mounted, setMounted] = React.useState(false);
  const progressPercentage = Math.min(
    100,
    Math.max(0, (currentPoints / pointsForNextLevel) * 100)
  );
  const pointsNeeded = Math.max(0, pointsForNextLevel - currentPoints);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const SPRING = {
    type: 'spring' as const,
    damping: 10,
    mass: 0.75,
    stiffness: 100,
  };

  return (
    <div
      className={cn(
        'w-full overflow-hidden rounded-xl border-2 border-gray-200 dark:border-[#8a2be2]/30 shadow-lg',
        className
      )}
    >
      <div className="bg-gradient-to-br from-[#6a0dad] to-[#8a2be2] p-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10" />
        
        <div className="relative z-10 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white/80 font-montserrat">Bem-vindo,</p>
              <h2 className="text-2xl font-bold font-ubuntu">{userName}</h2>
            </div>
            <span className="inline-flex items-center px-4 py-2 rounded-lg text-lg font-bold bg-white/20 text-white border border-white/30 font-ubuntu">
              Nível {currentLevel}
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className="font-medium font-montserrat">Progresso para Nível {currentLevel + 1}</span>
              <span className="font-bold font-ubuntu">
                {Math.round(progressPercentage)}%
              </span>
            </div>

            <div className="relative h-4 w-full overflow-hidden rounded-full bg-white/20 border border-white/30">
              {mounted && (
                <motion.div
                  className="h-full rounded-full shadow-lg bg-gradient-to-r from-[#e0e1dd] to-[#778da9]"
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={SPRING}
                />
              )}
            </div>

            <div className="flex justify-between items-center text-xs text-white/90 font-montserrat">
              <span>
                <AnimatedNumber value={currentPoints} className="font-bold" /> pontos
              </span>
              <span>
                <AnimatedNumber value={pointsForNextLevel} className="font-bold" /> pontos
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 bg-white dark:bg-[#1b263b]">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="space-y-1">
            <p className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
              <AnimatedNumber value={currentLevel} />
            </p>
            <p className="text-xs text-gray-600 dark:text-[#778da9] font-montserrat">Nível Atual</p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
              <AnimatedNumber value={currentPoints} />
            </p>
            <p className="text-xs text-gray-600 dark:text-[#778da9] font-montserrat">Pontos Atuais</p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-gray-900 dark:text-[#e0e1dd] font-ubuntu">
              <AnimatedNumber value={pointsNeeded} />
            </p>
            <p className="text-xs text-gray-600 dark:text-[#778da9] font-montserrat">Faltam</p>
          </div>
        </div>
      </div>
    </div>
  );
}
